// src/utils/file.utils.ts
import type { FileMetadata } from '@/types/poc/helia';

const CID_PREVIEW_LENGTH = 8;

/**
 * Extracts original filename by removing encryption-related extensions
 */
export function extractOriginalFileName(encryptedFileName: string): string {
  return encryptedFileName.replace(/\.(encrypted|key\.encrypted)$/, '');
}

export interface CreateFileMetadataOptions {
  cid: string;
  size: number;
  sixId: string;
  providedMetadata?: FileMetadata;
  now?: number;
}

/**
 * Factory for a FileMetadata record, used by network-download flows that
 * learn about a file only by its CID. Non-deterministic because `lastModified`
 * defaults to `Date.now()`; pass `now` explicitly in tests. Per UTILITIES_GUIDE,
 * this is allowed here because the non-determinism only affects a display
 * field — not crypto, routing, or persisted key material.
 */
export function createFileMetadata({
  cid,
  size,
  sixId,
  providedMetadata,
  now = Date.now(),
}: CreateFileMetadataOptions): FileMetadata {
  if (providedMetadata !== undefined) {
    return { ...providedMetadata, size };
  }

  return {
    id: cid,
    cid,
    name: `file-${cid.substring(0, CID_PREVIEW_LENGTH)}`,
    size,
    path: '',
    type: 'application/octet-stream',
    lastModified: now,
    addedBy: 'unknown',
    sixId,
    uploadedByUserId: 'unknown',
    uploadedByUserName: 'unknown',
  };
}

/**
 * Extracts original file type from encrypted filename
 */
export function extractOriginalFileType(encryptedFileName: string): string {
  const originalFileName = extractOriginalFileName(encryptedFileName);
  return getFileTypeFromExtension(originalFileName);
}

/**
 * Determines MIME type from file extension
 */
export function getFileTypeFromExtension(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf('.');

  if (lastDotIndex === -1) {
    return 'application/octet-stream';
  }

  const extension = fileName.substring(lastDotIndex + 1).toLowerCase();

  // Common MIME type mappings
  const mimeTypeMap: Record<string, string> = {
    // Text
    'txt': 'text/plain',
    'csv': 'text/csv',
    'html': 'text/html',
    'css': 'text/css',
    'js': 'application/javascript',
    'json': 'application/json',
    'xml': 'application/xml',

    // Documents
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'ppt': 'application/vnd.ms-powerpoint',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',

    // Images
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'bmp': 'image/bmp',
    'webp': 'image/webp',
    'svg': 'image/svg+xml',
    'ico': 'image/x-icon',

    // Audio
    'mp3': 'audio/mpeg',
    'wav': 'audio/wav',
    'ogg': 'audio/ogg',

    // Video
    'mp4': 'video/mp4',
    'avi': 'video/x-msvideo',
    'mov': 'video/quicktime',
    'webm': 'video/webm',

    // Archives
    'zip': 'application/zip',
    'rar': 'application/x-rar-compressed',
    '7z': 'application/x-7z-compressed',
    'tar': 'application/x-tar',
    'gz': 'application/gzip'
  };

  return mimeTypeMap[extension] ?? 'application/octet-stream';
}

/**
 * Generates encrypted filename from original
 */
export function generateEncryptedFileName(originalFileName: string): string {
  return `${originalFileName}.encrypted`;
}

/**
 * Generates encrypted key filename from original
 */
export function generateEncryptedKeyFileName(originalFileName: string): string {
  return `${originalFileName}.key.encrypted`;
}
